/**
 *
 * @deprecated use color-mix(in oklch, var(--textDefault-body) 30%, transparent);
 */
export const hexToRgbString = (hex: string) => {
  const bigint = parseInt(hex.slice(1), 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r}, ${g}, ${b}`
}

type RGB = { r: number; g: number; b: number }

/**
 * Converts a single RGB channel value (0-255) to hex
 */
const toHex = (value: number): string => value.toString(16).padStart(2, "0")

/**
 * Converts RGB values to hex string
 */
const rgbToHex = ({ r, g, b }: RGB): string => `#${toHex(r)}${toHex(g)}${toHex(b)}`

/**
 * Applies gamma correction for linear sRGB to sRGB conversion
 */
const linearToSrgb = (x: number): number => {
  const absX = Math.abs(x)
  return absX > 0.0031308 ? Math.sign(x) * (1.055 * Math.pow(absX, 1 / 2.4) - 0.055) : 12.92 * x
}

/**
 * Clamps a value between 0 and 1, then converts to 0-255 range
 */
const clampAndScale = (value: number): number => Math.round(Math.max(0, Math.min(1, value)) * 255)

/**
 * Converts OKLCH to RGB
 * @param l - Lightness (0-1)
 * @param c - Chroma
 * @param h - Hue (0-360)
 */
function oklchToRgb(l: number, c: number, h: number): RGB {
  const hRad = (h / 360) * 2 * Math.PI

  // OKLCH to Oklab (polar to cartesian)
  const a = c * Math.cos(hRad)
  const b = c * Math.sin(hRad)

  // Oklab to linear sRGB
  const lms = {
    l: (l + 0.3963377774 * a + 0.2158037573 * b) ** 3,
    m: (l - 0.1055613458 * a - 0.0638541728 * b) ** 3,
    s: (l - 0.0894841775 * a - 1.291485548 * b) ** 3,
  }

  return {
    r: clampAndScale(linearToSrgb(+4.0767416621 * lms.l - 3.3077115913 * lms.m + 0.2309699292 * lms.s)),
    g: clampAndScale(linearToSrgb(-1.2684380046 * lms.l + 2.6097574011 * lms.m - 0.3413193965 * lms.s)),
    b: clampAndScale(linearToSrgb(-0.0041960863 * lms.l - 0.7034186147 * lms.m + 1.707614701 * lms.s)),
  }
}
/**
 * Parses oklch() CSS function and returns RGB
 */
function parseOklch(value: string): RGB | null {
  const match = value.match(/oklch\(([\d.]+)%?\s+([\d.]+)\s+([\d.]+)\)/i)
  if (!match?.[1] || !match?.[2] || !match?.[3]) return null

  let l = parseFloat(match[1])
  if (l > 1) l /= 100 // Convert percentage to 0-1 range

  return oklchToRgb(l, parseFloat(match[2]), parseFloat(match[3]))
}

/**
 * Parses rgb()/rgba() CSS function and returns RGB
 */
function parseRgb(value: string): RGB | null {
  const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (!match?.[1] || !match?.[2] || !match?.[3]) return null

  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
  }
}

/**
 * Converts CSS color values to hex format.
 * Supports: oklch, rgb/rgba, and hex formats.
 */
export function cssColorToHex(colorValue: string): string {
  const trimmed = colorValue.trim()

  // Already hex
  if (/^#?[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`
  }

  // Try oklch
  const oklchRgb = parseOklch(trimmed)
  if (oklchRgb) return rgbToHex(oklchRgb)

  // Try rgb/rgba
  const rgb = parseRgb(trimmed)
  if (rgb) return rgbToHex(rgb)

  console.warn(`Unable to convert color value to hex: ${trimmed}`)
  return trimmed
}
